import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const DEFAULT_GST = 18;

// Read the logged-in username from the JWT stored in localStorage
const getLoggedInUsername = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.username || payload.name || null;
  } catch {
    return null;
  }
};

const InwardFormModal = ({ onClose, onSaved }) => {
  const [openPos, setOpenPos] = useState([]);
  const [loadingPos, setLoadingPos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedPoId, setSelectedPoId] = useState('');
  const [selectedPo, setSelectedPo] = useState(null);

  const [form, setForm] = useState({
    purchase_date: new Date().toISOString().split('T')[0],
    bill_no: '',
    gst_percent: DEFAULT_GST,
    rider_name: '',
    rider_cnic: '',
    rider_phone: '',
    vehicle_no: '',
    received_by: '',
    amount_paid: '',
    payment_mode: 'cash',
    notes: '',
    // items are derived from the PO; each row holds receiving inputs
    items: [],
  });

  // Load open POs (pending / partial)
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingPos(true);
        const res = await chemicalService.getOpenPurchaseOrders();
        setOpenPos(res.data || []);
      } catch (err) {
        console.error('Error loading open POs:', err);
        alert('❌ Failed to load open purchase orders');
      } finally {
        setLoadingPos(false);
      }
    };
    load();
  }, []);

  // When a PO is selected, build item rows from its open lines
  const handleSelectPo = (poId) => {
    setSelectedPoId(poId);
    if (!poId) {
      setSelectedPo(null);
      setForm((prev) => ({ ...prev, items: [], gst_percent: DEFAULT_GST }));
      return;
    }
    const po = openPos.find((p) => String(p.id) === String(poId));
    setSelectedPo(po || null);
    if (po) {
      const items = (po.items || []).map((it) => {
        const remaining = parseFloat(it.remaining_qty) || 0;
        return {
          po_item_id: it.id,
          product_id: it.product_id,
          product_code: it.product_code,
          product_name: it.product_name,
          unit: it.unit,
          rate: parseFloat(it.rate) || 0,
          gst_percent: parseFloat(it.gst_percent) || DEFAULT_GST,
          ordered_qty: parseFloat(it.quantity) || 0,
          remaining_qty: remaining,
          receiving_qty: remaining,   // default to full remaining
          batch_no: '',
          expiry_date: '',
        };
      });
      setForm((prev) => ({ ...prev, items }));
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  // ─── Live totals (per-line GST, only counting items with receiving qty > 0) ──
  const lineAmount = (it) => (parseFloat(it.receiving_qty) || 0) * (parseFloat(it.rate) || 0);
  const lineGst = (it) => lineAmount(it) * ((parseFloat(it.gst_percent) || 0) / 100);
  const subtotal = form.items.reduce((sum, it) => sum + lineAmount(it), 0);
  const gstAmount = form.items.reduce((sum, it) => sum + lineGst(it), 0);
  const grandTotal = subtotal + gstAmount;
  const amountPaid = parseFloat(form.amount_paid) || 0;
  const balance = grandTotal - amountPaid;

  const fmtMoney = (n) => 'Rs ' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-PK');
  const fmtQty = (n) => parseFloat(n || 0).toFixed(2).replace(/\.?0+$/, '');

  // Will this receipt fully or partially fill the PO?
  const receivingStatus = () => {
    if (!selectedPo) return null;
    const totalRemaining = form.items.reduce((s, it) => s + (parseFloat(it.remaining_qty) || 0), 0);
    const totalReceiving = form.items.reduce((s, it) => s + (parseFloat(it.receiving_qty) || 0), 0);
    if (totalReceiving <= 0) return null;
    return totalReceiving >= totalRemaining ? 'Received' : 'Partially Received';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!selectedPoId) return alert('❌ Select a purchase order.');
    if (!form.bill_no.trim()) return alert('❌ Bill number is required.');
    if (!form.purchase_date) return alert('❌ Date is required.');
    if (!form.rider_name.trim()) return alert('❌ Rider name is required.');
    if (!form.vehicle_no.trim()) return alert('❌ Vehicle number is required.');
    if (!form.received_by.trim()) return alert('❌ Received by is required.');

    // Items: at least one line must be receiving > 0, and batch no required for those
    const receivingItems = form.items.filter((it) => (parseFloat(it.receiving_qty) || 0) > 0);
    if (receivingItems.length === 0) return alert('❌ Enter a receiving quantity for at least one item.');

    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      const recv = parseFloat(it.receiving_qty) || 0;
      const idx = i + 1;
      if (recv < 0) return alert(`❌ Item ${idx}: receiving quantity cannot be negative.`);
      if (recv > it.remaining_qty + 1e-9) return alert(`❌ Item ${idx}: cannot receive more than remaining (${fmtQty(it.remaining_qty)}).`);
      if (recv > 0 && !it.batch_no.trim()) return alert(`❌ Item ${idx}: batch number is required.`);
    }

    if (amountPaid > grandTotal) return alert('❌ Amount paid cannot exceed grand total.');

    try {
      setSubmitting(true);
      const payload = {
        bill_no: form.bill_no.trim(),
        vendor_id: selectedPo.vendor_id,
        purchase_date: form.purchase_date,
        po_id: selectedPo.id,
        subtotal: Math.round(subtotal * 100) / 100,
        gst_amount: Math.round(gstAmount * 100) / 100,
        gst_percent: parseFloat(form.gst_percent) || 0,
        discount: 0,
        grand_total: Math.round(grandTotal * 100) / 100,
        amount_paid: amountPaid,
        payment_mode: form.payment_mode,
        notes: form.notes.trim() || null,
        rider_name: form.rider_name.trim(),
        rider_cnic: form.rider_cnic.trim() || null,
        rider_phone: form.rider_phone.trim() || null,
        vehicle_no: form.vehicle_no.trim(),
        received_by: form.received_by.trim(),
        feed_by: getLoggedInUsername(),
        // only send lines actually being received
        items: receivingItems.map((it) => ({
          po_item_id: it.po_item_id,
          product_id: it.product_id,
          batch_no: it.batch_no.trim(),
          mfg_date: form.purchase_date,
          expiry_date: it.expiry_date || null,
          quantity: parseFloat(it.receiving_qty),
          unit: it.unit,
          rate: parseFloat(it.rate),
          amount: Math.round(lineAmount(it) * 100) / 100,
        })),
      };

      const res = await chemicalService.createInward(payload);
      const gpNo = res.data?.gate_pass_no || '';
      alert(`✅ Inward saved! Purchase + stock + gate pass created.${gpNo ? `\nGate Pass: ${gpNo}` : ''}`);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save inward';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const status = receivingStatus();

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>↓ New Inward (Receive against PO)</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Records the goods received, stock-in, and gate pass together
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* Step 1 — Select PO */}
            <div style={styles.sectionLabel}>Step 1 · Select Purchase Order</div>
            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>Purchase Order <span style={styles.required}>*</span></label>
                <select
                  value={selectedPoId}
                  onChange={(e) => handleSelectPo(e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingPos}
                >
                  <option value="">
                    {loadingPos ? 'Loading open POs...' : openPos.length === 0 ? '-- No open purchase orders --' : '-- Select a PO --'}
                  </option>
                  {openPos.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.po_no} · {po.vendor_name} · {fmtMoney(po.grand_total)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedPo && (
              <>
                <div style={styles.row}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Vendor (from PO)</label>
                    <div style={styles.lockedField}>🔒 {selectedPo.vendor_name}</div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Date <span style={styles.required}>*</span></label>
                    <input type="date" value={form.purchase_date} onChange={(e) => handleChange('purchase_date', e.target.value)} style={styles.input} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Bill No <span style={styles.required}>*</span></label>
                    <input type="text" value={form.bill_no} onChange={(e) => handleChange('bill_no', e.target.value)} style={styles.input} placeholder="e.g. BILL-014" required />
                  </div>
                </div>

                {/* Step 2 — Items to receive */}
                <div style={styles.sectionLabel}>Step 2 · Items to Receive</div>
                <div style={styles.itemsList}>
                  {form.items.map((item, index) => (
                    <div key={index} style={styles.itemCard}>
                      <div style={styles.itemHeader}>
                        <div style={styles.itemIndex}>{index + 1}</div>
                        <span style={styles.itemTitle}>{item.product_code} — {item.product_name}</span>
                        <span style={styles.lineAmount}>{fmtMoney(lineAmount(item))}</span>
                      </div>

                      <div style={styles.itemRow}>
                        <div style={{ flex: 1, minWidth: '80px' }}>
                          <label style={styles.miniLabel}>Ordered</label>
                          <div style={styles.readOnlyMini}>{fmtQty(item.ordered_qty)} {item.unit}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: '80px' }}>
                          <label style={styles.miniLabel}>Remaining</label>
                          <div style={styles.readOnlyMini}>{fmtQty(item.remaining_qty)} {item.unit}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: '100px' }}>
                          <label style={styles.miniLabel}>Receiving Now *</label>
                          <input
                            type="number" step="0.01" min="0" max={item.remaining_qty}
                            value={item.receiving_qty}
                            onChange={(e) => handleItemChange(index, 'receiving_qty', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: '90px' }}>
                          <label style={styles.miniLabel}>Rate (locked)</label>
                          <div style={styles.readOnlyMini}>{fmtMoney(item.rate)}</div>
                        </div>
                      </div>

                      <div style={{ ...styles.itemRow, marginTop: '6px' }}>
                        <div style={{ flex: 2, minWidth: '140px' }}>
                          <label style={styles.miniLabel}>Batch No *</label>
                          <input type="text" value={item.batch_no} onChange={(e) => handleItemChange(index, 'batch_no', e.target.value)} style={styles.input} placeholder="e.g. CHM-26-014" />
                        </div>
                        <div style={{ flex: 1, minWidth: '140px' }}>
                          <label style={styles.miniLabel}>Expiry Date (optional)</label>
                          <input type="date" value={item.expiry_date} onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)} style={styles.input} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Step 3 — Delivery & payment */}
                <div style={styles.sectionLabel}>Step 3 · Delivery & Payment</div>
                <div style={styles.row}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Rider Name <span style={styles.required}>*</span></label>
                    <input type="text" value={form.rider_name} onChange={(e) => handleChange('rider_name', e.target.value)} style={styles.input} placeholder="e.g. Akram" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Vehicle No <span style={styles.required}>*</span></label>
                    <input type="text" value={form.vehicle_no} onChange={(e) => handleChange('vehicle_no', e.target.value)} style={styles.input} placeholder="e.g. LEB-1234" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Received By <span style={styles.required}>*</span></label>
                    <input type="text" value={form.received_by} onChange={(e) => handleChange('received_by', e.target.value)} style={styles.input} placeholder="Person who received goods" />
                  </div>
                </div>
                <div style={styles.row}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Rider CNIC</label>
                    <input type="text" value={form.rider_cnic} onChange={(e) => handleChange('rider_cnic', e.target.value)} style={styles.input} placeholder="optional" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Rider Phone</label>
                    <input type="tel" value={form.rider_phone} onChange={(e) => handleChange('rider_phone', e.target.value)} style={styles.input} placeholder="optional" />
                  </div>
                </div>

                <div style={styles.row}>
                  <div style={{ flex: 1, minWidth: '240px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Amount Paid Now</label>
                      <input type="number" step="0.01" min="0" value={form.amount_paid} onChange={(e) => handleChange('amount_paid', e.target.value)} style={styles.input} placeholder="0" />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Payment Mode</label>
                      <select value={form.payment_mode} onChange={(e) => handleChange('payment_mode', e.target.value)} style={styles.input}>
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                  </div>

                  <div style={styles.totalsBox}>
                    <div style={styles.totalRow}><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
                    <div style={styles.totalRow}><span>GST</span><span>{fmtMoney(gstAmount)}</span></div>
                    <div style={styles.totalRowGrand}><span>Grand Total</span><span>{fmtMoney(grandTotal)}</span></div>
                    <div style={styles.totalRowBalance}><span>Balance</span><span>{fmtMoney(balance)}</span></div>
                  </div>
                </div>

                <div style={styles.sectionLabel}>Notes</div>
                <div style={styles.formGroup}>
                  <textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }} placeholder="Optional notes..." rows="2" />
                </div>
              </>
            )}
          </div>

          <div style={styles.modalFooter}>
            {status && (
              <span style={styles.statusHint}>
                This receipt → PO becomes <strong>{status}</strong>
              </span>
            )}
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
            <button type="submit" style={styles.submitButton} disabled={submitting || !selectedPo}>
              {submitting ? '⏳ Saving...' : '✓ Save Inward'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '1000px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '20px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: { fontSize: '11px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  lockedField: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', background: '#f8f9fa', color: '#7f8c8d' },
  readOnlyMini: { padding: '10px 12px', border: '1px solid #eee', borderRadius: '8px', fontSize: '13px', background: '#f8f9fa', color: '#2c3e50' },
  itemsList: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' },
  itemCard: { background: '#f8f9fa', borderRadius: '10px', padding: '14px', border: '1px solid #e0e0e0' },
  itemHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px dashed #e0e0e0' },
  itemIndex: { width: '24px', height: '24px', borderRadius: '50%', background: '#8e44ad', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  itemTitle: { fontSize: '13px', fontWeight: '700', color: '#2c3e50', flex: 1 },
  lineAmount: { fontSize: '14px', fontWeight: '700', color: '#2c3e50' },
  itemRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  totalsBox: { flex: 1, minWidth: '240px', background: '#f8f9fa', borderRadius: '10px', padding: '14px 18px', height: 'fit-content' },
  totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#7f8c8d', padding: '4px 0' },
  totalRowGrand: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#2c3e50', padding: '8px 0 4px', borderTop: '1px solid #e0e0e0', marginTop: '4px' },
  totalRowBalance: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '700', color: '#e74c3c', padding: '4px 0' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center', position: 'sticky', bottom: 0, background: 'white' },
  statusHint: { marginRight: 'auto', fontSize: '13px', color: '#7f8c8d' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default InwardFormModal;